import { useEffect, useState } from "react";
import { NEUTRAL_TONE, toneFromPixels, type ArtworkTone } from "@/utils/pageBackgrounds";

const SAMPLE_EDGE = 24;
const TONE_WAIT_MS = 3000;
const tones = new Map<string, Promise<ArtworkTone>>();

function sampleTone(url: string): Promise<ArtworkTone> {
  const cached = tones.get(url);
  if (cached) return cached;
  const pending = new Promise<ArtworkTone>((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE_EDGE;
        canvas.height = SAMPLE_EDGE;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return resolve(NEUTRAL_TONE);
        ctx.drawImage(image, 0, 0, SAMPLE_EDGE, SAMPLE_EDGE);
        resolve(toneFromPixels(ctx.getImageData(0, 0, SAMPLE_EDGE, SAMPLE_EDGE).data));
      } catch {
        resolve(NEUTRAL_TONE);
      }
    };
    image.onerror = () => resolve(NEUTRAL_TONE);
    image.src = url;
  });
  tones.set(url, pending);
  return pending;
}

export function useArtworkTone(url: string | null | undefined): ArtworkTone | undefined {
  const [state, setState] = useState<{ url: string; tone: ArtworkTone } | null>(null);
  useEffect(() => {
    if (!url) return;
    let settled = false;
    const finish = (tone: ArtworkTone) => {
      if (settled) return;
      settled = true;
      setState({ url, tone });
    };
    const timer = window.setTimeout(() => finish(NEUTRAL_TONE), TONE_WAIT_MS);
    sampleTone(url).then(finish);
    return () => {
      settled = true;
      window.clearTimeout(timer);
    };
  }, [url]);
  if (!url) return undefined;
  return state?.url === url ? state.tone : undefined;
}
